/**
 * Admin customer view — spot upsell opportunities across the customer base.
 *
 * Uses the shared AdminGate + AdminHeader so it fits inside the same admin
 * area as /admin/briefs, /admin/businesses, /admin/requests (no separate
 * login, no separate nav).
 *
 * Layout:
 *  - Table row per business: name/trade/city, signed-up date, AI Visibility
 *    score, social channel badges, Meta connect status + followers, offer
 *    count, contact info.
 *  - Row click → detail panel below the row (full snapshot on one page, no
 *    click-throughs).
 *  - Manual outreach only for v1: mailto:/tel: links.
 *
 * Defensive rendering: every field that comes from a nested document uses
 * optional chaining + fallbacks. If a business is missing socialPresence,
 * metaPage, or offers, the row still renders.
 */

import { useEffect, useState } from "react";
import { Loader2, Check, Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { listAdminCustomers, scheduleBriefForBusiness, getAdminToken } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import AdminGate from "@/components/AdminGate";

export default function AdminCustomers() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  if (!authed) return <AdminGate onAuthed={() => setAuthed(true)} redirect="/admin/customers" />;
  return <AdminCustomersInner />;
}

function AdminCustomersInner() {
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [sending, setSending] = useState(() => new Set());

  const load = async () => {
    setBusy(true);
    try {
      const list = await listAdminCustomers();
      setCustomers(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error("Couldn't load customers.", { description: err?.message });
      setCustomers([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const scheduleBrief = async (businessId) => {
    if (!businessId || sending.has(businessId)) return;
    setSending((prev) => new Set(prev).add(businessId));
    try {
      const { results } = await scheduleBriefForBusiness(businessId);
      const r = (results || [])[0] || {};
      if (r.status === "scheduled") {
        toast.success(`Scheduled: ${r.name}`, { description: r.detail });
        // Reflect the new state locally without a full reload.
        setCustomers((prev) =>
          (prev || []).map((c) => c.id === businessId ? { ...c, hasScheduledBrief: true } : c)
        );
      } else if (r.status === "skipped_dedupe") {
        toast.info(`Already queued: ${r.name}`, { description: r.detail });
        setCustomers((prev) =>
          (prev || []).map((c) => c.id === businessId ? { ...c, hasScheduledBrief: true } : c)
        );
      } else if (r.status === "skipped_placeholder_email") {
        toast.warning(`Skipped: no email on file for ${r.name}`);
      } else if (r.status === "skipped_opted_out") {
        toast.warning(`Skipped: ${r.name} is paused or unsubscribed.`);
      } else {
        toast.error(`Couldn't schedule ${r.name || businessId}`, { description: r.detail });
      }
    } catch (err) {
      toast.error("Schedule request failed.", { description: err?.message });
    } finally {
      setSending((prev) => {
        const next = new Set(prev);
        next.delete(businessId);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-customers-page">
      <AdminHeader onRefresh={load} refreshing={busy} active="customers" />

      <main className="w-full max-w-screen-xl mx-auto px-5 sm:px-8 py-8 page-fade">
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Admin · Customer view
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1"
          style={{ color: "var(--ta-primary)" }}
        >
          Customers {customers ? `(${customers.length})` : ""}
        </h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--ta-text-secondary)" }}>
          Every business signed up so far — sorted newest first. Click a row to
          expand suggested upsells, Facebook Page data, and contact details.
        </p>

        {busy && !customers && (
          <div className="mt-10 flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {customers && customers.length === 0 && (
          <div className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--ta-border)", background: "#fff" }}>
            <p className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>
              No customers yet.
            </p>
          </div>
        )}

        {customers && customers.length > 0 && (
          <div
            className="mt-6 rounded-2xl overflow-hidden border-2"
            style={{ background: "var(--ta-surface)", borderColor: "var(--ta-border)" }}
          >
            <table className="w-full text-sm">
              <thead style={{ background: "var(--ta-surface-alt)" }}>
                <tr>
                  {["Business", "Signed up", "AI", "Social", "Meta", "Offers", "Contact", "Brief"].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider"
                      style={{ color: "var(--ta-primary)" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <CustomerRow
                    key={c?.id || Math.random()}
                    c={c || {}}
                    expanded={expanded === c?.id}
                    sending={sending.has(c?.id)}
                    onToggle={() => setExpanded(expanded === c?.id ? null : c?.id)}
                    onScheduleBrief={() => scheduleBrief(c?.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

const CustomerRow = ({ c, expanded, sending, onToggle, onScheduleBrief }) => {
  const channels = c?.socialPresence?.channels || [];
  const chBadge = (name) => {
    const ch = Array.isArray(channels) ? channels.find((x) => x?.channel === name) : null;
    if (!ch || !ch.urlProvided) {
      return <span title={`No ${name}`} style={{ opacity: 0.35 }}>—</span>;
    }
    return (
      <span title={ch.url || ""} style={{ color: "#1c4322" }}>
        <Check size={14} className="inline" strokeWidth={2.5} />
      </span>
    );
  };

  const signedUp = c?.signedUpAt ? safeDate(c.signedUpAt) : "—";
  const offerCount = Array.isArray(c?.applicableOffers) ? c.applicableOffers.length : 0;
  const dupSize = c?.duplicateGroupSize || 1;
  const hasScheduled = !!c?.hasScheduledBrief;

  return (
    <>
      <tr
        data-testid={`admin-customer-row-${c?.id || "unknown"}`}
        onClick={onToggle}
        className="cursor-pointer border-t hover:bg-black/[.02] transition-colors"
        style={{ borderColor: "var(--ta-border)" }}
      >
        <td className="px-3 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold" style={{ color: "var(--ta-primary)" }}>
              {c?.businessName || "—"}
            </div>
            {dupSize > 1 && (
              <span
                data-testid={`admin-customer-duplicate-badge-${c?.id}`}
                title={`${dupSize} businesses share this exact name — dedupe before sending`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ background: "#fbe9e7", color: "#a12b2b" }}
              >
                <AlertTriangle size={11} strokeWidth={2.5} />
                Duplicate × {dupSize}
              </span>
            )}
          </div>
          <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
            {(c?.trade || "—")} · {(c?.city || "—")}{c?.state ? `, ${c.state}` : ""}
          </div>
        </td>
        <td className="px-3 py-3 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
          {signedUp}
        </td>
        <td className="px-3 py-3 font-semibold">
          {c?.aiVisibilityScore != null ? c.aiVisibilityScore : "—"}
        </td>
        <td className="px-3 py-3 space-x-2 text-xs whitespace-nowrap">
          <span>FB {chBadge("facebook")}</span>
          <span>IG {chBadge("instagram")}</span>
          <span>GBP {chBadge("google_business_profile")}</span>
        </td>
        <td className="px-3 py-3 text-xs">
          {c?.metaConnected ? (
            <div>
              <div style={{ color: "#1c4322" }} className="font-semibold">Connected</div>
              {c?.metaPage?.followers != null && (
                <div style={{ color: "var(--ta-text-secondary)" }}>
                  {c.metaPage.followers} followers
                </div>
              )}
            </div>
          ) : (
            <span style={{ opacity: 0.5 }}>—</span>
          )}
        </td>
        <td className="px-3 py-3 font-semibold">{offerCount}</td>
        <td className="px-3 py-3 text-xs">
          {c?.email && (
            <a
              href={`mailto:${c.email}`}
              onClick={(e) => e.stopPropagation()}
              style={{ color: "var(--ta-primary)" }}
              className="underline"
            >
              {c.email}
            </a>
          )}
          {c?.phone && (
            <div style={{ color: "var(--ta-text-secondary)" }}>{c.phone}</div>
          )}
          {!c?.email && !c?.phone && (
            <span style={{ opacity: 0.4 }}>—</span>
          )}
        </td>
        <td className="px-3 py-3 text-xs">
          {hasScheduled ? (
            <span
              data-testid={`admin-customer-brief-queued-${c?.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold"
              style={{ color: "#1c4322" }}
              title="A brief is already queued in the next 8 days"
            >
              <CheckCircle2 size={14} strokeWidth={2.5} />
              Queued
            </span>
          ) : (
            <button
              type="button"
              data-testid={`admin-customer-schedule-brief-${c?.id}`}
              onClick={(e) => { e.stopPropagation(); onScheduleBrief?.(); }}
              disabled={sending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              style={{
                borderColor: "var(--ta-primary)",
                color: "var(--ta-primary)",
                background: "#fff",
              }}
            >
              {sending ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  Scheduling…
                </>
              ) : (
                <>
                  <Send size={12} strokeWidth={2.5} />
                  Send Monday{"'"}s brief
                </>
              )}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="px-6 py-5" style={{ background: "var(--ta-surface-alt)" }}>
            <CustomerDetail c={c} />
          </td>
        </tr>
      )}
    </>
  );
};

const CustomerDetail = ({ c }) => {
  const offers = Array.isArray(c?.applicableOffers) ? c.applicableOffers : [];
  const meta = c?.metaPage || null;
  const recentPosts = Array.isArray(meta?.recentPosts) ? meta.recentPosts : [];

  return (
    <div data-testid={`admin-customer-detail-${c?.id || "unknown"}`} className="space-y-5 text-sm">
      <section>
        <h3 className="font-semibold mb-1.5" style={{ color: "var(--ta-primary)" }}>
          Suggested upsells ({offers.length})
        </h3>
        {offers.length ? (
          <ul className="list-disc list-inside space-y-1">
            {offers.map((o, i) => (
              <li key={o?.id || i}>
                <span className="font-semibold">{o?.title || "Untitled offer"}</span>
                {o?.trigger && (
                  <span style={{ color: "var(--ta-text-secondary)" }}> — {o.trigger}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: "var(--ta-text-secondary)" }}>
            No gaps detected. Nothing to pitch.
          </div>
        )}
      </section>

      {c?.metaConnected && meta && (
        <section>
          <h3 className="font-semibold mb-1.5" style={{ color: "var(--ta-primary)" }}>
            Facebook Page
          </h3>
          <div>
            <span className="font-semibold">{meta.pageName || "Unnamed page"}</span>
            {" · "}
            {meta.followers != null ? `${meta.followers} followers` : "followers unknown"}
          </div>
          {meta.lastPostAt && (
            <div style={{ color: "var(--ta-text-secondary)" }}>
              Last post: {safeDate(meta.lastPostAt)}
            </div>
          )}
          {meta.about && (
            <div className="text-xs mt-1" style={{ color: "var(--ta-text-secondary)" }}>
              {meta.about}
            </div>
          )}
          {meta.link && (
            <div className="mt-1">
              <a
                href={meta.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--ta-primary)" }}
                className="underline break-all"
              >
                {meta.link}
              </a>
            </div>
          )}
          {recentPosts.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold mb-1">Recent posts:</div>
              <ul className="space-y-1 text-xs">
                {recentPosts.map((p, i) => (
                  <li key={i}>
                    {p?.createdTime ? safeDate(p.createdTime) : "—"}:{" "}
                    {p?.message ? String(p.message).slice(0, 140) : "(no text)"}
                    {p?.permalinkUrl && (
                      <>
                        {" · "}
                        <a
                          href={p.permalinkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--ta-primary)" }}
                          className="underline"
                        >
                          view
                        </a>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <section>
        <h3 className="font-semibold mb-1.5" style={{ color: "var(--ta-primary)" }}>
          Contact
        </h3>
        {c?.email && (
          <div>
            <a href={`mailto:${c.email}`} style={{ color: "var(--ta-primary)" }} className="underline">
              {c.email}
            </a>
          </div>
        )}
        {c?.phone && (
          <div>
            <a href={`tel:${c.phone}`} style={{ color: "var(--ta-primary)" }} className="underline">
              {c.phone}
            </a>
          </div>
        )}
        {!c?.email && !c?.phone && (
          <div style={{ color: "var(--ta-text-secondary)" }}>No contact info on file.</div>
        )}
      </section>

      <section className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        Business ID: <code>{c?.id || "—"}</code>
      </section>
    </div>
  );
};

// Small helper — never let a bad date string crash the row.
function safeDate(v) {
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
}
